import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    NewspaperIcon,
    ClockIcon,
    ChevronRightIcon,
    ArrowPathIcon,
    CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiService from '../services/api';
import MarketOverview from '../components/MarketOverview';
import timeAgo from '../utils/timeAgo';
import REFRESH_INTERVALS, { useAutoRefresh } from '../config/refreshConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { reportMarkdownComponents } from '../config/markdownComponents';
import ErrorMessage from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import XFlowsDecisionBrief from '../components/XFlowsDecisionBrief';

function formatEventDate(dateStr, language) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((d - today) / 86400000);
    if (diffDays === 0) return language === 'zh' ? '今天' : 'Today';
    if (diffDays === 1) return language === 'zh' ? '明天' : 'Tomorrow';
    const locale = language === 'zh' ? 'zh-CN' : 'en-US';
    const weekday = d.toLocaleDateString(locale, { weekday: 'short' });
    const month = d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    return `${weekday}, ${month}`;
}

const HOUR_LABELS_EN = { bmo: 'Pre-market', amc: 'After-close', dmh: 'During' };
const HOUR_LABELS_ZH = { bmo: '盘前', amc: '盘后', dmh: '盘中' };

const HOUR_BADGE_COLORS = {
    bmo: { bg: 'rgba(0,122,255,0.10)', color: '#007AFF' },
    amc: { bg: 'rgba(175,82,222,0.10)', color: '#AF52DE' },
    dmh: { bg: 'rgba(255,159,10,0.10)', color: '#FF9F0A' },
};

const CATEGORY_LABELS_ZH = {
    mag7: 'Mag 7', semi: '半导体', software: '软件', china: '中概',
    fintech: '金融科技', biotech: '生物科技', energy: '能源', consumer: '消费', index: '指数', other: '其他',
};
const CATEGORY_LABELS_EN = {
    mag7: 'Mag 7', semi: 'Semi', software: 'Software', china: 'China',
    fintech: 'Fintech', biotech: 'Biotech', energy: 'Energy', consumer: 'Consumer', index: 'Index', other: 'Other',
};

function formatRevenue(value) {
    if (value == null) return null;
    const num = Math.abs(value);
    if (num >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (num >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
    if (num >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
    return `$${value}`;
}

// ======== Report parser ========

const COMMON_WORDS = new Set([
    'THE', 'AND', 'FOR', 'ARE', 'NOT', 'YOU', 'ALL', 'CAN', 'HAS', 'HER',
    'WAS', 'ONE', 'OUR', 'OUT', 'HIS', 'HAS', 'ITS', 'LET', 'SAY', 'SHE',
    'TOO', 'USE', 'CEO', 'CFO', 'COO', 'CTO', 'IPO', 'GDP', 'FED', 'SEC',
    'ETF', 'EPS', 'YOY', 'QOQ', 'USA', 'FDA', 'API', 'AWS', 'CPU', 'GPU',
    'RAM', 'SSD', 'LED', 'IoT', 'IOT', 'AI', 'ML', 'PR', 'IT', 'OR',
    'AN', 'AT', 'BY', 'DO', 'GO', 'IF', 'IN', 'IS', 'MY', 'NO', 'OF',
    'ON', 'SO', 'TO', 'UP', 'WE', 'US', 'VS', 'PE', 'EU', 'UK',
    'MAY', 'NEW', 'NOW', 'OLD', 'SEE', 'WAY', 'WHO', 'BOY', 'DID', 'GET',
    'HIM', 'HOW', 'MAN', 'OIL', 'SIT', 'TRY', 'ASK', 'BIG', 'END',
    'FAR', 'FEW', 'GOT', 'HAD', 'HIT', 'HOT', 'LOW', 'MET', 'RAN', 'RED',
    'RUN', 'SET', 'TOP', 'WON', 'YET', 'AGO', 'DAY', 'KEY', 'CEO',
    'EST', 'REV', 'R&D', 'PMI', 'CPI', 'PCE', 'DOJ', 'FBI', 'CIA', 'NSA',
    'NYSE', 'FOMC', 'EBITDA', 'GAAP',
]);

// Company name → ticker mapping for extracting tickers from narrative text
const COMPANY_TO_TICKER = {
    // Mag 7
    'Apple': 'AAPL', 'Microsoft': 'MSFT', 'Google': 'GOOGL', 'Alphabet': 'GOOGL',
    'Amazon': 'AMZN', 'Meta': 'META', 'Facebook': 'META', 'NVIDIA': 'NVDA',
    'Tesla': 'TSLA',
    // Semi
    'Broadcom': 'AVGO', 'Intel': 'INTC', 'Qualcomm': 'QCOM', 'Micron': 'MU',
    'TSMC': 'TSM', 'ASML': 'ASML',
    // Software
    'Salesforce': 'CRM', 'Oracle': 'ORCL', 'Palantir': 'PLTR', 'Snowflake': 'SNOW',
    'CrowdStrike': 'CRWD', 'Cloudflare': 'NET', 'Shopify': 'SHOP', 'Datadog': 'DDOG',
    'MongoDB': 'MDB',
    // Fintech / Crypto
    'Coinbase': 'COIN', 'Robinhood': 'HOOD', 'PayPal': 'PYPL', 'Block': 'SQ',
    'Galaxy Digital': 'GLXY', 'Marathon Digital': 'MARA', 'MicroStrategy': 'MSTR',
    'Riot Platforms': 'RIOT', 'Riot': 'RIOT',
    'Goldman Sachs': 'GS', 'JPMorgan': 'JPM', 'Morgan Stanley': 'MS',
    // China
    'Alibaba': 'BABA', 'Baidu': 'BIDU', 'Tencent': 'TCEHY',
    'BYD': 'BYDDY', 'Pinduoduo': 'PDD',
    // Consumer
    'Netflix': 'NFLX', 'Disney': 'DIS', 'Airbnb': 'ABNB', 'Uber': 'UBER',
    'Starbucks': 'SBUX',
    // Biotech
    'Eli Lilly': 'LLY', 'Moderna': 'MRNA', 'Pfizer': 'PFE',
    // Chinese names
    '英伟达': 'NVDA', '苹果': 'AAPL', '特斯拉': 'TSLA', '微软': 'MSFT',
    '谷歌': 'GOOGL', '亚马逊': 'AMZN', '台积电': 'TSM', '英特尔': 'INTC',
    '高通': 'QCOM', '博通': 'AVGO', '奈飞': 'NFLX', '阿里巴巴': 'BABA',
    '百度': 'BIDU', '腾讯': 'TCEHY', '比亚迪': 'BYDDY', '拼多多': 'PDD',
    '星巴克': 'SBUX',
};

// Ticker → category mapping for grouping
const TICKER_CATEGORY = {
    // Indices & ETFs
    QQQ: 'index', SPY: 'index', DIA: 'index', IWM: 'index', VIX: 'index',
    SOXX: 'index', SMH: 'index', XLK: 'index', XLF: 'index', XLE: 'index',
    XLV: 'index', ARKK: 'index', TLT: 'index', HYG: 'index', GLD: 'index',
    // Mag 7
    AAPL: 'mag7', MSFT: 'mag7', GOOGL: 'mag7', GOOG: 'mag7', AMZN: 'mag7',
    META: 'mag7', NVDA: 'mag7', TSLA: 'mag7',
    // Semiconductor
    AMD: 'semi', AVGO: 'semi', INTC: 'semi', QCOM: 'semi', MU: 'semi',
    MRVL: 'semi', LRCX: 'semi', KLAC: 'semi', AMAT: 'semi', ASML: 'semi',
    TSM: 'semi', ARM: 'semi', ON: 'semi', MCHP: 'semi', TXN: 'semi',
    ADI: 'semi', NXPI: 'semi', SWKS: 'semi',
    // Software / Cloud
    CRM: 'software', ORCL: 'software', NOW: 'software', ADBE: 'software',
    SNOW: 'software', PLTR: 'software', DDOG: 'software', NET: 'software',
    CRWD: 'software', ZS: 'software', PANW: 'software', FTNT: 'software',
    MDB: 'software', SHOP: 'software', SQ: 'software', TWLO: 'software',
    HUBS: 'software', WDAY: 'software', TEAM: 'software', OKTA: 'software',
    S: 'software', PATH: 'software', BILL: 'software', CFLT: 'software',
    MNDY: 'software', DOCN: 'software', APP: 'software', RBLX: 'software',
    U: 'software', ESTC: 'software', PINS: 'software', SNAP: 'software',
    // China tech
    BABA: 'china', JD: 'china', PDD: 'china', BIDU: 'china', NIO: 'china',
    XPEV: 'china', LI: 'china', BILI: 'china', TME: 'china', NTES: 'china',
    TCOM: 'china', VIPS: 'china', MNSO: 'china', IQ: 'china',
    TCEHY: 'china', BYDDY: 'china',
    // Fintech / Financial
    COIN: 'fintech', HOOD: 'fintech', AFRM: 'fintech', SOFI: 'fintech',
    V: 'fintech', MA: 'fintech', PYPL: 'fintech', GS: 'fintech',
    JPM: 'fintech', MS: 'fintech', BAC: 'fintech', WFC: 'fintech',
    // Crypto mining / digital assets
    MARA: 'fintech', MSTR: 'fintech', RIOT: 'fintech', GLXY: 'fintech',
    // Biotech / Health
    LLY: 'biotech', NVO: 'biotech', MRNA: 'biotech', ABBV: 'biotech',
    PFE: 'biotech', JNJ: 'biotech', UNH: 'biotech', ISRG: 'biotech',
    DXCM: 'biotech', ILMN: 'biotech',
    // Energy / Industrial
    XOM: 'energy', CVX: 'energy', SLB: 'energy', NEE: 'energy',
    ENPH: 'energy', FSLR: 'energy', LNG: 'energy',
    // Consumer
    NFLX: 'consumer', DIS: 'consumer', ABNB: 'consumer', UBER: 'consumer',
    LYFT: 'consumer', DASH: 'consumer', SBUX: 'consumer', NKE: 'consumer',
    COST: 'consumer', WMT: 'consumer', TGT: 'consumer',
    // Memory / Storage (sub-semi but distinct)
    WDC: 'semi', STX: 'semi', HBM: 'semi',
};

const CATEGORY_META = {
    index:    { label: 'Indices & Macro',  icon: '📊', order: 0 },
    mag7:     { label: 'Magnificent 7',    icon: '🏆', order: 1 },
    semi:     { label: 'Semiconductor',    icon: '🔬', order: 2 },
    software: { label: 'Software & Cloud', icon: '☁️', order: 3 },
    china:    { label: 'China Tech',       icon: '🇨🇳', order: 4 },
    fintech:  { label: 'Fintech',          icon: '💳', order: 5 },
    biotech:  { label: 'Biotech & Health', icon: '🧬', order: 6 },
    energy:   { label: 'Energy',           icon: '⚡', order: 7 },
    consumer: { label: 'Consumer',         icon: '🛍️', order: 8 },
    other:    { label: 'Other',            icon: '📌', order: 9 },
};

// Sentiment detection helpers
const UP_KW = ['涨', '反弹', '回购', '提升', '增长', '突破', '利好', '看好', '上升', '推动', '创新', '获批', '飙升', '回升', '走强', '买入', '增持', '稳定', '拓展', 'rally', 'surge', 'gain', 'rise', 'bull'];
const DOWN_KW = ['跌', '减持', '暴跌', '下降', '承压', '禁止', '施压', '限制', '风险', '谨慎', '抛售', '走弱', '卖出', '恐慌', 'fell', 'drop', 'decline', 'loss', 'bear', 'sell'];

function inferSentiment(text) {
    const t = text.toLowerCase();
    const upCount = UP_KW.filter(w => t.includes(w.toLowerCase())).length;
    const downCount = DOWN_KW.filter(w => t.includes(w.toLowerCase())).length;
    if (upCount > downCount) return 'up';
    if (downCount > upCount) return 'down';
    return 'neutral';
}

// Extract the sentence containing a keyword at position `idx` in the text
function extractSentence(text, idx, keywordLen) {
    const delimiters = /[。！？；\n]/;
    let start = idx;
    while (start > 0 && !delimiters.test(text[start - 1])) start--;
    let end = idx + keywordLen;
    while (end < text.length && !delimiters.test(text[end])) end++;
    return text.substring(start, end).replace(/[#*_>`|]/g, '').replace(/\s+/g, ' ').trim();
}

function parseReportContent(markdown) {
    if (!markdown || markdown.length < 200) {
        return { tickers: [], sections: [], summary: '' };
    }

    // Extract summary: text before the first ## heading
    const firstH2 = markdown.indexOf('\n## ');
    const summaryRaw = firstH2 > 0 ? markdown.substring(0, firstH2) : '';
    const summary = summaryRaw
        .replace(/[#*_>\-`]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 250);

    // Split into sections by ## headings
    const sectionRegex = /^## (.+)$/gm;
    const sectionStarts = [];
    let match;
    while ((match = sectionRegex.exec(markdown)) !== null) {
        sectionStarts.push({ title: match[1].trim(), index: match.index });
    }

    const sections = sectionStarts.map((sec, i) => {
        const start = sec.index;
        const end = i < sectionStarts.length - 1 ? sectionStarts[i + 1].index : markdown.length;
        const content = markdown.substring(start, end).trim();
        const sectionTickers = [];
        const tickerRe = /\b([A-Z]{2,5})\b/g;
        let tm;
        while ((tm = tickerRe.exec(content)) !== null) {
            if (!COMMON_WORDS.has(tm[1]) && !sectionTickers.includes(tm[1])) {
                sectionTickers.push(tm[1]);
            }
        }
        const excerpt = content
            .replace(/^## .+\n*/m, '')
            .replace(/[#*_>\-`|]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .substring(0, 120);
        return { title: sec.title, content, excerpt, tickers: sectionTickers.slice(0, 6) };
    });

    // ---- Phase 1: Extract tickers with explicit percentage changes ----
    const tickerMap = {};
    const lines = markdown.split('\n');
    for (const line of lines) {
        const percentMatches = [...line.matchAll(/\b([A-Z]{2,5})\b.*?([+-]?\d+\.?\d*)%/g)];
        for (const pm of percentMatches) {
            const symbol = pm[1];
            if (COMMON_WORDS.has(symbol)) continue;
            if (tickerMap[symbol]) continue;
            const pct = parseFloat(pm[2]);
            if (isNaN(pct)) continue;
            const fullMatch = pm[0];
            let signedPct = pct;

            // Check for explicit +/- signs in the match
            if (fullMatch.includes('-' + Math.abs(pct))) {
                signedPct = -Math.abs(pct);
            } else if (fullMatch.includes('+' + Math.abs(pct))) {
                signedPct = Math.abs(pct);
            } else {
                // Check context around the ticker (before the percentage) for sentiment keywords
                // Use the matched text which contains ticker...percentage
                const contextText = fullMatch;
                const upKwInContext = /上涨|涨|上升|反弹|飙升|回升|走强|gain|rise|rally|surge|jump/i.test(contextText);
                const downKwInContext = /下跌|跌|下降|暴跌|走弱|fell|drop|decline|loss|down|slip|tumbl/i.test(contextText);

                if (downKwInContext && !upKwInContext) {
                    signedPct = -Math.abs(pct);
                }
                // If upKwInContext or neither, keep positive (default assumption for percentages)
            }

            const cleanLine = line.replace(/[#*_>`|]/g, '').replace(/\s+/g, ' ').trim();
            const context = cleanLine.length > 150 ? cleanLine.substring(0, 147) + '...' : cleanLine;

            tickerMap[symbol] = {
                symbol,
                percentChange: signedPct,
                sentiment: signedPct >= 0 ? 'up' : 'down',
                context,
                category: TICKER_CATEGORY[symbol] || 'other',
            };
        }
    }

    // ---- Collect all sentences mentioning each ticker/company ----
    // Build a reverse map: ticker → [company names that map to it]
    const tickerNames = {};
    for (const [name, sym] of Object.entries(COMPANY_TO_TICKER)) {
        if (!sym) continue;
        if (!tickerNames[sym]) tickerNames[sym] = [];
        tickerNames[sym].push(name);
    }

    // Split full text into sentences for searching
    const allSentences = markdown.split(/(?<=[。！？；\n])/g)
        .map(s => s.replace(/[#*_>`|]/g, '').replace(/\s+/g, ' ').trim())
        .filter(s => s.length > 10);

    function findAllSentences(symbol) {
        const names = tickerNames[symbol] || [];
        const keywords = [symbol, ...names];
        return allSentences.filter(s => keywords.some(k => s.includes(k)));
    }

    // ---- Phase 2: Extract tickers from company name mentions ----
    for (const [companyName, ticker] of Object.entries(COMPANY_TO_TICKER)) {
        if (!ticker || tickerMap[ticker]) continue;
        const idx = markdown.indexOf(companyName);
        if (idx === -1) continue;
        const sentence = extractSentence(markdown, idx, companyName.length);
        const context = sentence.length > 150 ? sentence.substring(0, 147) + '...' : sentence;
        const related = findAllSentences(ticker);

        tickerMap[ticker] = {
            symbol: ticker,
            percentChange: null,
            sentiment: inferSentiment(related.join(' ') || sentence),
            context,
            details: related,
            category: TICKER_CATEGORY[ticker] || 'other',
        };
    }

    // ---- Phase 3: Extract standalone known tickers without percentages ----
    const allTickerMatches = [...markdown.matchAll(/\b([A-Z]{2,5})\b/g)];
    for (const tm of allTickerMatches) {
        const symbol = tm[1];
        if (COMMON_WORDS.has(symbol) || tickerMap[symbol]) continue;
        if (!TICKER_CATEGORY[symbol]) continue;
        const sentence = extractSentence(markdown, tm.index, symbol.length);
        const context = sentence.length > 150 ? sentence.substring(0, 147) + '...' : sentence;
        const related = findAllSentences(symbol);
        tickerMap[symbol] = {
            symbol,
            percentChange: null,
            sentiment: inferSentiment(related.join(' ') || sentence),
            context,
            details: related,
            category: TICKER_CATEGORY[symbol] || 'other',
        };
    }

    // Also add details to Phase 1 tickers
    for (const t of Object.values(tickerMap)) {
        if (!t.details) t.details = findAllSentences(t.symbol);
    }

    const tickers = Object.values(tickerMap);

    return { tickers, sections, summary };
}

// ======== Styles ========

const styles = {
    card: {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        fontSize: '1.1rem',
        fontWeight: '600',
        color: '#1D1D1F',
    },
    cardContent: {
        flex: 1,
        overflowY: 'auto',
        paddingRight: '0.5rem',
    },
    newsItem: {
        padding: '0.6rem 0',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        cursor: 'pointer',
    },
};

// ======== Component ========

const HomePage = () => {
    const navigate = useNavigate();
    const { language, t } = useLanguage();
    const isZh = language === 'zh';
    const hourLabels = isZh ? HOUR_LABELS_ZH : HOUR_LABELS_EN;
    const [cachedHome] = useState(() => apiService.peekCache('homepage'));
    const [cachedEvents] = useState(() => apiService.peekCache('events:14'));
    const [cachedDailySummary] = useState(() => apiService.peekCache('daily-summary:postmarket'));
    const [loading, setLoading] = useState(!cachedHome);
    const [error, setError] = useState(null);
    const [headlines, setHeadlines] = useState(cachedHome?.data?.headlines || []);
    const [dailyReport, setDailyReport] = useState(cachedDailySummary?.data || null);
    const [reportLoading, setReportLoading] = useState(!cachedDailySummary);
    const [reportError, setReportError] = useState(null);
    const [upcomingEvents, setUpcomingEvents] = useState(cachedEvents?.data || []);
    const [eventsLoading, setEventsLoading] = useState(!cachedEvents);
    const [eventsError, setEventsError] = useState(null);
    const [showFullReport, setShowFullReport] = useState(false);
    const [expandedTicker, setExpandedTicker] = useState(null);

    const fetchData = useCallback(async () => {
        setError(null);
        try {
            const res = await apiService.getHomepageData();
            if (res?.status === 'success' && res.data) {
                setHeadlines(res.data.headlines || []);
            }
        } catch (err) {
            console.error('Failed to load homepage data:', err);
            setError(isZh ? '数据加载失败' : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [isZh]);

    const fetchDailyReport = useCallback(async () => {
        setReportError(null);
        try {
            const res = await apiService.getDailySummary('postmarket');
            if (res?.status === 'success') {
                setDailyReport(res.data || null);
            } else {
                setReportError(isZh ? '盘后简报加载失败' : 'Failed to load post-market briefing');
            }
        } catch (err) {
            console.error('Failed to load post-market briefing:', err);
            setReportError(isZh ? '盘后简报加载失败' : 'Failed to load post-market briefing');
        } finally {
            setReportLoading(false);
        }
    }, [isZh]);

    const fetchEvents = useCallback(async () => {
        setEventsError(null);
        try {
            const res = await apiService.getUpcomingEvents(14);
            if (res?.status === 'success') {
                setUpcomingEvents(res.data || []);
            }
        } catch (err) {
            console.error('Failed to load upcoming events:', err);
            setEventsError(isZh ? '事件加载失败' : 'Failed to load events');
        } finally {
            setEventsLoading(false);
        }
    }, [isZh]);

    useEffect(() => {
        fetchData();
        fetchDailyReport();
        fetchEvents();
    }, [fetchData, fetchDailyReport, fetchEvents]);

    useAutoRefresh(() => {
        fetchData();
        fetchDailyReport();
        fetchEvents();
    }, REFRESH_INTERVALS.home.data);

    // --- Report helpers ---
    const rawContent = dailyReport?.summary || dailyReport?.content || dailyReport?.report_content || '';
    const reportTitle = dailyReport?.title || dailyReport?.report_title
        || (isZh ? 'XFlows 盘后简报' : 'XFlows Post-market Briefing');
    const reportDateValue = dailyReport?.date || dailyReport?.report_date;
    const reportDate = reportDateValue
        ? new Date(`${reportDateValue}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : '';
    const reportContent = rawContent
        .replace(/^#\s+.+\n+/, '')
        .replace(/^[═─━─\s📊🔹🔸]*美股.+日\s*[═─━─\s]*\n*/gm, '')
        .replace(/^[═─━─]{3,}.*\n*/gm, '')
        .trim();
    const previewText = reportContent.length > 600 ? reportContent.substring(0, 600) + '...' : reportContent;

    const parsedReport = useMemo(() => parseReportContent(reportContent), [reportContent]);

    const effectiveTickers = parsedReport.tickers;

    const hasCards = effectiveTickers.length > 0 || parsedReport.sections.length > 0;
    const upCount = effectiveTickers.filter(tk => tk.sentiment === 'up').length;
    const downCount = effectiveTickers.filter(tk => tk.sentiment === 'down').length;

    // Group tickers by category, sorted by CATEGORY_META.order
    const groupedTickers = useMemo(() => {
        const groups = {};
        for (const tk of effectiveTickers) {
            const cat = tk.category || 'other';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(tk);
        }
        return Object.entries(groups)
            .sort((a, b) => (CATEGORY_META[a[0]]?.order ?? 99) - (CATEGORY_META[b[0]]?.order ?? 99));
    }, [effectiveTickers]);

    // Group events by date
    const eventsByDate = {};
    upcomingEvents.forEach(e => {
        const date = e.date || 'TBD';
        if (!eventsByDate[date]) eventsByDate[date] = [];
        eventsByDate[date].push(e);
    });

    return (
        <div>
            {/* Top Row: Market (left) + Headlines (right) */}
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <MarketOverview />
                <div style={{ ...styles.card, maxHeight: '380px' }}>
                    <div style={styles.cardHeader}>
                        <span>{t('home.headlines')}</span>
                        <ClockIcon style={{ width: '20px', color: '#FF9F0A' }} />
                    </div>
                    <div style={styles.cardContent}>
                        {loading ? (
                            <LoadingSpinner text={t('home.loading')} size="medium" />
                        ) : error ? (
                            <ErrorMessage message={error} onRetry={fetchData} size="small" />
                        ) : headlines.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#86868B', padding: '2rem 0' }}>{t('home.noHeadlines')}</div>
                        ) : (
                            headlines.map((news, idx) => {
                                const titleStr = typeof news.title === 'string' ? news.title : '';
                                const titleZh = typeof news.title_zh === 'string' ? news.title_zh : '';
                                // Chinese fallback: title_zh → first line of ai_summary → English title
                                const summaryFirstLine = news.ai_summary ? news.ai_summary.split('\n')[0].replace(/^[•\-\s]+/, '') : '';
                                const displayTitle = isZh ? (titleZh || summaryFirstLine || titleStr) : titleStr;
                                const subtitle = isZh ? (titleZh ? titleStr : '') : (titleZh || null);
                                return (
                                <div
                                    key={news.id || idx}
                                    style={styles.newsItem}
                                    onClick={() => news.id ? navigate(`/feed/${news.id}`) : null}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            color: '#007AFF',
                                            textTransform: 'uppercase'
                                        }}>{news.source || 'unknown'}</span>
                                        <span style={{ fontSize: '0.75rem', color: '#86868B' }}>{timeAgo(news.published_at)}</span>
                                    </div>
                                    <div style={{ fontSize: '0.9rem', fontWeight: '500', lineHeight: '1.35' }}>
                                        {displayTitle}
                                    </div>
                                    {subtitle && (
                                        <div style={{
                                            fontSize: '0.78rem',
                                            color: '#555',
                                            lineHeight: '1.4',
                                            marginTop: '4px',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}>
                                            {subtitle}
                                        </div>
                                    )}
                                </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* Refresh Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                <button
                    onClick={() => { fetchData(); fetchDailyReport(); fetchEvents(); }}
                    disabled={loading || reportLoading}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: loading || reportLoading ? 'default' : 'pointer',
                        padding: '8px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#86868B',
                    }}
                    title={t('home.refresh')}
                    aria-label={t('home.refresh')}
                >
                    <ArrowPathIcon style={{ width: '20px', animation: loading || reportLoading ? 'spin 1s linear infinite' : 'none' }} aria-hidden="true" />
                </button>
            </div>

            {/* Content Grid: events + daily briefing */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(0, 2fr)', gap: '1.5rem', minHeight: '500px' }}>

                {/* ====== Left Column: Upcoming Events ====== */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ ...styles.card, flex: 1 }}>
                        <div style={styles.cardHeader}>
                            <span>{t('home.upcomingEvents')}</span>
                            <CalendarDaysIcon style={{ width: '20px', color: '#FF9F0A' }} />
                        </div>
                        <div style={styles.cardContent}>
                            {eventsLoading ? (
                                <LoadingSpinner text={t('home.loading')} size="medium" />
                            ) : eventsError ? (
                                <ErrorMessage message={eventsError} onRetry={fetchEvents} size="small" />
                            ) : upcomingEvents.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#86868B', padding: '2rem 0' }}>{t('home.noEvents')}</div>
                            ) : (
                                Object.entries(eventsByDate).map(([date, events]) => {
                                    const catLabels = isZh ? CATEGORY_LABELS_ZH : CATEGORY_LABELS_EN;
                                    return (
                                    <div key={date} style={{ marginBottom: '1.2rem' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            color: '#86868B',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.03em',
                                            marginBottom: '8px',
                                            paddingBottom: '6px',
                                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                                        }}>
                                            <div style={{
                                                width: '3px',
                                                height: '14px',
                                                borderRadius: '2px',
                                                background: '#FF9F0A',
                                            }} />
                                            {formatEventDate(date, language)}
                                        </div>
                                        {events.map((event, idx) => {
                                            const cat = TICKER_CATEGORY[event.symbol] || 'other';
                                            const catLabel = catLabels[cat] || catLabels.other;
                                            const hourStyle = HOUR_BADGE_COLORS[event.hour] || HOUR_BADGE_COLORS.dmh;
                                            const revStr = formatRevenue(event.revenue_estimate);
                                            const hasBeat = event.eps_actual != null;
                                            const beat = hasBeat && event.eps_actual >= (event.eps_estimate || 0);
                                            return (
                                            <div key={`${event.symbol}-${idx}`} style={{
                                                background: 'rgba(0,0,0,0.02)',
                                                borderRadius: '10px',
                                                padding: '10px 12px',
                                                marginBottom: '6px',
                                                transition: 'background 0.15s',
                                                cursor: 'default',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.02)'; }}
                                            >
                                                {/* Top row: ticker + category | estimates */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            background: '#007AFF',
                                                            color: '#fff',
                                                            fontSize: '0.78rem',
                                                            fontWeight: '700',
                                                            padding: '2px 8px',
                                                            borderRadius: '6px',
                                                            letterSpacing: '0.02em',
                                                        }}>
                                                            {event.symbol}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '0.68rem',
                                                            fontWeight: '600',
                                                            color: '#86868B',
                                                            background: 'rgba(0,0,0,0.04)',
                                                            padding: '1px 6px',
                                                            borderRadius: '4px',
                                                        }}>
                                                            {catLabel}
                                                        </span>
                                                    </div>
                                                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                        {event.eps_estimate != null && (
                                                            <div style={{ fontSize: '0.75rem', color: '#86868B' }}>
                                                                EPS <span style={{ fontWeight: '700', color: hasBeat ? (beat ? '#34C759' : '#FF3B30') : '#1D1D1F' }}>
                                                                    ${hasBeat ? event.eps_actual : event.eps_estimate}
                                                                </span>
                                                                {hasBeat && (
                                                                    <span style={{
                                                                        marginLeft: '4px',
                                                                        fontSize: '0.68rem',
                                                                        fontWeight: '700',
                                                                        color: beat ? '#34C759' : '#FF3B30',
                                                                    }}>
                                                                        {beat ? '✓ Beat' : '✗ Miss'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {revStr && (
                                                            <div style={{ fontSize: '0.72rem', color: '#86868B' }}>
                                                                {t('home.revEstimate')} <span style={{ fontWeight: '600', color: '#1D1D1F' }}>{revStr}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* Bottom row: timing badge + quarter */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {event.hour && (
                                                        <span style={{
                                                            fontSize: '0.68rem',
                                                            fontWeight: '600',
                                                            color: hourStyle.color,
                                                            background: hourStyle.bg,
                                                            padding: '2px 8px',
                                                            borderRadius: '10px',
                                                        }}>
                                                            {hourLabels[event.hour] || t('home.earnings')}
                                                        </span>
                                                    )}
                                                    {event.quarter && event.year && (
                                                        <span style={{ fontSize: '0.68rem', color: '#86868B' }}>
                                                            Q{event.quarter} {event.year}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                    );
                                }))
                            }
                        </div>
                    </div>
                </div>

                {/* ====== Center Column: Daily Briefing ====== */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ ...styles.card, flex: 1 }}>
                        <div style={styles.cardHeader}>
                            <span>{t('home.dailyBriefing')}</span>
                            <NewspaperIcon style={{ width: '20px', color: '#1D1D1F' }} />
                        </div>
                        <div style={styles.cardContent}>
                            {reportLoading ? (
                                <LoadingSpinner text={t('home.loadingReport')} size="large" />
                            ) : reportError ? (
                                <ErrorMessage message={reportError} onRetry={fetchDailyReport} size="small" />
                            ) : !dailyReport ? (
                                <div style={{ textAlign: 'center', color: '#86868B', padding: '3rem 0' }}>{t('home.noReport')}</div>
                            ) : dailyReport.decision_brief ? (
                                <XFlowsDecisionBrief brief={dailyReport.decision_brief} language={language} />
                            ) : !hasCards ? (
                                /* Fallback: plain markdown when no structured data extracted */
                                <>
                                    <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: '1rem' }}>
                                        <h2 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '0.5rem', lineHeight: '1.2' }}>
                                            {reportTitle}
                                        </h2>
                                        <div style={{ display: 'flex', gap: '10px', fontSize: '0.8rem', color: '#86868B' }}>
                                            <span>{reportDate}</span>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '0.95rem', lineHeight: '1.7', color: '#333' }}>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={reportMarkdownComponents}>
                                            {showFullReport ? reportContent : previewText}
                                        </ReactMarkdown>
                                    </div>
                                    {reportContent.length > 600 && (
                                        <button
                                            onClick={() => setShowFullReport(!showFullReport)}
                                            style={{
                                                backgroundColor: '#1D1D1F', color: 'white', border: 'none',
                                                padding: '8px 20px', borderRadius: '20px', fontSize: '0.85rem',
                                                fontWeight: '600', cursor: 'pointer', display: 'flex',
                                                alignItems: 'center', gap: '6px', marginTop: '1rem',
                                                alignSelf: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            }}
                                        >
                                            {showFullReport ? t('home.collapse') : t('home.readFullReport')}
                                            <ChevronRightIcon style={{ width: '14px', transform: showFullReport ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s ease' }} />
                                        </button>
                                    )}
                                </>
                            ) : (
                                /* Card flow layout */
                                <>
                                    {/* Hero area */}
                                    <div style={{ marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                                            <h2 style={{ fontSize: '1.35rem', fontWeight: '800', lineHeight: '1.2', margin: 0, color: '#1D1D1F' }}>
                                                {reportTitle}
                                            </h2>
                                            <span style={{ fontSize: '0.78rem', color: '#86868B', whiteSpace: 'nowrap', marginLeft: '12px', marginTop: '4px' }}>{reportDate}</span>
                                        </div>
                                        {parsedReport.summary && typeof parsedReport.summary === 'string' && (
                                            <p style={{ fontSize: '0.88rem', color: '#555', lineHeight: '1.5', margin: '0.5rem 0 0' }}>
                                                {parsedReport.summary}
                                            </p>
                                        )}
                                        {effectiveTickers.length > 0 && (
                                            <div style={{ fontSize: '0.75rem', color: '#86868B', marginTop: '0.5rem' }}>
                                                {effectiveTickers.length} {t('home.companies')}
                                                {upCount > 0 && <span style={{ color: '#34C759', marginLeft: '8px' }}>{upCount} {t('home.up')}</span>}
                                                {downCount > 0 && <span style={{ color: '#FF3B30', marginLeft: '8px' }}>{downCount} {t('home.down')}</span>}
                                            </div>
                                        )}
                                    </div>

                                    {/* Grouped Impact Cards */}
                                    {groupedTickers.map(([cat, tickers]) => {
                                        const meta = CATEGORY_META[cat] || CATEGORY_META.other;
                                        return (
                                            <div key={cat} style={{ marginBottom: '1.25rem' }}>
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: '6px',
                                                    marginBottom: '8px', paddingBottom: '6px',
                                                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                                                }}>
                                                    <span style={{ fontSize: '0.9rem' }}>{meta.icon}</span>
                                                    <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1D1D1F', letterSpacing: '0.01em' }}>
                                                        {meta.label}
                                                    </span>
                                                    <span style={{ fontSize: '0.72rem', color: '#86868B', marginLeft: '4px' }}>
                                                        {tickers.length}
                                                    </span>
                                                </div>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                                    gap: '8px',
                                                }}>
                                                    {tickers.map((t) => {
                                                        const isExpanded = expandedTicker === t.symbol;
                                                        const cardBg = t.sentiment === 'up' ? 'rgba(52,199,89,0.06)'
                                                            : t.sentiment === 'down' ? 'rgba(255,59,48,0.06)' : 'rgba(0,0,0,0.02)';
                                                        const cardBorder = t.sentiment === 'up' ? 'rgba(52,199,89,0.12)'
                                                            : t.sentiment === 'down' ? 'rgba(255,59,48,0.12)' : 'rgba(0,0,0,0.06)';
                                                        const sentimentColor = t.sentiment === 'up' ? '#34C759'
                                                            : t.sentiment === 'down' ? '#FF3B30' : '#86868B';
                                                        return (
                                                            <div key={t.symbol} onClick={() => setExpandedTicker(isExpanded ? null : t.symbol)} style={{
                                                                padding: '12px 14px', borderRadius: '14px',
                                                                backgroundColor: cardBg,
                                                                border: `1px solid ${cardBorder}`,
                                                                cursor: 'pointer',
                                                                transition: 'box-shadow 0.15s ease',
                                                                boxShadow: isExpanded ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
                                                                gridColumn: isExpanded ? '1 / -1' : undefined,
                                                            }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                                    <span style={{
                                                                        fontSize: '0.78rem', fontWeight: '700',
                                                                        padding: '2px 7px', borderRadius: '6px',
                                                                        backgroundColor: 'rgba(0,122,255,0.1)', color: '#007AFF',
                                                                    }}>
                                                                        {typeof t.symbol === 'string' ? t.symbol : ''}
                                                                    </span>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                        {t.percentChange != null ? (
                                                                            <span style={{ fontSize: '0.95rem', fontWeight: '700', color: sentimentColor }}>
                                                                                {t.percentChange >= 0 ? '+' : ''}{t.percentChange.toFixed(1)}%
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ fontSize: '0.78rem', fontWeight: '600', color: sentimentColor }}>
                                                                                {t.sentiment === 'up' ? (isZh ? '▲ 看多' : '▲ Bullish') : t.sentiment === 'down' ? (isZh ? '▼ 看空' : '▼ Bearish') : (isZh ? '— 中性' : '— Neutral')}
                                                                            </span>
                                                                        )}
                                                                        <ChevronRightIcon style={{ width: 12, color: '#86868B', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }} />
                                                                    </div>
                                                                </div>
                                                                {!isExpanded ? (
                                                                    <div style={{
                                                                        fontSize: '0.75rem', color: '#555', lineHeight: '1.4',
                                                                        display: '-webkit-box', WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                                    }}>
                                                                        {t.title || (typeof t.context === 'string' ? t.context : '')}
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ fontSize: '0.8rem', color: '#333', lineHeight: '1.6', marginTop: '4px' }}>
                                                                        {(t.details && t.details.length > 0 ? t.details : [t.context]).map((s, i) => (
                                                                            <p key={i} style={{ margin: '0 0 8px', paddingLeft: '8px', borderLeft: `2px solid ${sentimentColor}20` }}>{typeof s === 'string' ? s : ''}</p>
                                                                        ))}
                                                                        {t.catalysts && t.catalysts.length > 0 && (
                                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                                                                                {t.catalysts.map((c, i) => (
                                                                                    <span key={i} style={{
                                                                                        fontSize: '0.7rem', padding: '2px 8px', borderRadius: '10px',
                                                                                        backgroundColor: `${sentimentColor}10`, color: sentimentColor,
                                                                                        border: `1px solid ${sentimentColor}20`,
                                                                                    }}>{c}</span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Full Report toggle */}
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <button
                                            onClick={() => setShowFullReport(!showFullReport)}
                                            style={{
                                                backgroundColor: showFullReport ? '#86868B' : '#1D1D1F',
                                                color: 'white', border: 'none',
                                                padding: '8px 20px', borderRadius: '20px',
                                                fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            }}
                                        >
                                            {showFullReport ? t('home.hideFullReport') : t('home.viewFullReport')}
                                            <ChevronRightIcon style={{
                                                width: '14px',
                                                transform: showFullReport ? 'rotate(90deg)' : 'none',
                                                transition: 'transform 0.2s ease',
                                            }} />
                                        </button>
                                    </div>
                                    {showFullReport && (
                                        <div style={{ marginTop: '1rem', fontSize: '0.95rem', lineHeight: '1.7', color: '#333', borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: '1rem' }}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={reportMarkdownComponents}>
                                                {reportContent}
                                            </ReactMarkdown>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
